import React, { useState } from "react";
import { getStudentFeeData } from "../../utils/Api";
import { formatINR } from "../../utils/format";
import UnifiedReceiptViewer from "../UnifiedReceiptViewer";
import CasteAdjustmentNote from "./CasteAdjustmentNote";
import InstallmentProgress from "./InstallmentProgress";

// Status -> pill styling. green=paid, yellow=partial, red=pending (matches old page).
const STATUS_STYLES = {
  paid: "bg-green-100 text-green-800",
  partial: "bg-yellow-100 text-yellow-800",
  pending: "bg-red-100 text-red-800",
};

const statusPill = (status) => {
  const key = (status || "pending").toLowerCase();
  return STATUS_STYLES[key] || "bg-gray-100 text-gray-700";
};

/**
 * One timeline node rendered as a card.
 *
 * Handles two kinds of node:
 *  - kind="admission": the First-Year admission/application fee (node 0),
 *    driven entirely by applicationFeeData (getStudentApplicationFee).
 *  - kind="tuition": a single academic year's tuition, driven by a summary row
 *    from getAllStudentFees. Full details (breakdown, installments, payment
 *    gates) are lazily fetched via getStudentFeeData(academicYear) when expanded.
 *
 * @param {"admission"|"tuition"} kind
 * @param {object}   data          Summary fee object for this node
 * @param {string}   title         Card heading (e.g. "Second Year — 2024-25")
 * @param {boolean}  defaultExpanded
 * @param {boolean}  paymentLoading
 * @param {Function} onPayTuition  (feeStructureId, academicYear, type, installmentNumber) => void
 * @param {Function} onPayApplication (type, installmentNumber) => void
 */
const FeeYearCard = ({
  kind,
  data,
  title,
  defaultExpanded = false,
  paymentLoading = false,
  onPayTuition,
  onPayApplication,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showReceipts, setShowReceipts] = useState(false);

  const isAdmission = kind === "admission";

  const remaining = data?.remainingAmount ?? 0;
  const paid = data?.paidAmount ?? 0;
  const payable = data?.totalAmount ?? data?.payableAmount ?? 0;
  const original = data?.originalAmount ?? payable;
  const discount = data?.discountPercentage ?? 0;
  const status = data?.paymentStatus || (remaining <= 0 ? "paid" : "pending");

  const fetchDetail = async () => {
    if (isAdmission || detail || !data?.academicYear) return;
    setDetailLoading(true);
    try {
      const res = await getStudentFeeData(data.academicYear);
      setDetail(res.data);
    } catch (error) {
      console.error("Error fetching fee detail:", error);
    } finally {
      setDetailLoading(false);
    }
  };

  const toggleExpanded = () => {
    const next = !expanded;
    setExpanded(next);
    if (next) fetchDetail();
  };

  // Load detail up-front when the card starts expanded (e.g. the current year).
  React.useEffect(() => {
    if (defaultExpanded && !isAdmission) fetchDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tuition payment gates come from the (lazily loaded) fee structure.
  const feeStructure = detail?.feeStructure;
  const isAcceptingPayments =
    feeStructure?.paymentAcceptance?.isAcceptingPayments ?? false;
  const allowInstallments =
    feeStructure?.paymentStructure?.allowInstallments ?? false;

  const receiptFilters = isAdmission
    ? { receiptType: "application" }
    : { receiptType: "student_fee", academicYear: data?.academicYear };

  const renderTuitionPayments = () => {
    if (remaining <= 0) return null;
    if (detailLoading || !detail) {
      return (
        <div className="flex justify-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
        </div>
      );
    }
    if (!isAcceptingPayments) {
      return (
        <div className="text-red-600 font-medium text-center py-3 text-sm">
          Fee payment is currently not allowed. Please contact the administration
          or try again later.
        </div>
      );
    }
    return (
      <div className="space-y-4">
        <button
          onClick={() =>
            onPayTuition(feeStructure._id, data.academicYear, "full", null)
          }
          disabled={paymentLoading}
          className="w-full bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          {paymentLoading
            ? "Processing..."
            : `Pay Full Amount (${formatINR(remaining)})`}
        </button>

        {allowInstallments && detail.installmentDetails?.length > 0 && (
          <div className="border-t pt-4">
            <h4 className="font-medium mb-3 text-gray-700">Pay by Installments</h4>
            <InstallmentProgress
              installments={detail.installmentDetails}
              onPay={(installmentNumber) =>
                onPayTuition(
                  feeStructure._id,
                  data.academicYear,
                  "installment",
                  installmentNumber,
                )
              }
              paymentLoading={paymentLoading}
              canPay={isAcceptingPayments}
              accent="green"
            />
          </div>
        )}
      </div>
    );
  };

  const renderAdmissionPayments = () => {
    if (remaining <= 0) return null;
    return (
      <div className="space-y-4">
        <button
          onClick={() => onPayApplication("full")}
          disabled={paymentLoading}
          className="w-full bg-orange-600 text-white py-3 px-4 rounded-md hover:bg-orange-700 disabled:opacity-50"
        >
          {paymentLoading
            ? "Processing..."
            : `Pay Remaining Amount (${formatINR(remaining)})`}
        </button>

        {data.installmentOptions?.isEnabled &&
          data.installmentOptions?.pendingInstallments?.length > 0 && (
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3 text-gray-700">
                Or Pay by Installment
              </h4>
              <InstallmentProgress
                installments={data.installmentOptions.pendingInstallments}
                onPay={(installmentNumber) =>
                  onPayApplication("installment", installmentNumber)
                }
                paymentLoading={paymentLoading}
                accent="orange"
              />
            </div>
          )}
      </div>
    );
  };

  return (
    <div
      className={`bg-white rounded-xl border shadow-sm overflow-hidden ${
        isAdmission ? "border-l-4 border-l-orange-500" : ""
      }`}
    >
      {/* Header (always visible, summary) */}
      <div className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3
              className={`text-lg font-semibold ${
                isAdmission ? "text-orange-700" : "text-gray-800"
              }`}
            >
              {title}
            </h3>
            {isAdmission ? (
              <p className="text-sm text-gray-500 mt-0.5">
                {data.applicationNumber}
                {data.course ? ` · ${data.course}` : ""}
              </p>
            ) : (
              <p className="text-sm text-gray-500 mt-0.5">Tuition Fee</p>
            )}
          </div>
          <span
            className={`shrink-0 px-2.5 py-1 rounded-full text-xs font-medium ${statusPill(
              status,
            )}`}
          >
            {(status || "").toUpperCase()}
          </span>
        </div>

        {/* Amount pills */}
        <div className="grid grid-cols-3 gap-3 mt-4">
          <div className="rounded-lg bg-gray-50 px-3 py-2">
            <p className="text-xs text-gray-500">Payable</p>
            <p className="font-semibold text-gray-800">
              {discount > 0 && (
                <span className="text-gray-400 line-through text-sm me-1">
                  {formatINR(original)}
                </span>
              )}
              {formatINR(payable)}
            </p>
          </div>
          <div className="rounded-lg bg-green-50 px-3 py-2">
            <p className="text-xs text-gray-500">Paid</p>
            <p className="font-semibold text-green-600">{formatINR(paid)}</p>
          </div>
          <div className="rounded-lg bg-red-50 px-3 py-2">
            <p className="text-xs text-gray-500">Remaining</p>
            <p className="font-semibold text-red-600">{formatINR(remaining)}</p>
          </div>
        </div>

        {discount > 0 && (
          <div className="mt-3">
            <CasteAdjustmentNote
              originalAmount={original}
              payableAmount={payable}
              discountPercentage={discount}
              category={data.category}
              label={isAdmission ? "admission fee" : "tuition fee"}
            />
          </div>
        )}

        {/* Admission node keeps its payments always visible (it's the CTA node). */}
        {isAdmission && remaining > 0 && (
          <div className="mt-4">{renderAdmissionPayments()}</div>
        )}

        {/* Expand toggle */}
        <div className="mt-4 flex flex-wrap items-center gap-4">
          {!isAdmission && (
            <button
              onClick={toggleExpanded}
              className="text-sm font-medium text-blue-600 hover:text-blue-700"
            >
              {expanded ? "Hide details" : "View details & pay"}
            </button>
          )}
          <button
            onClick={() => setShowReceipts((s) => !s)}
            className="text-sm font-medium text-gray-600 hover:text-gray-800"
          >
            {showReceipts ? "Hide receipts" : "View receipts"}
          </button>
        </div>
      </div>

      {/* Expanded detail (tuition breakdown + payments) */}
      {!isAdmission && expanded && (
        <div className="border-t bg-gray-50/60 p-5 space-y-5">
          {detailLoading && !detail ? (
            <div className="flex justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
            </div>
          ) : (
            detail?.feeStructure?.fees && (
              <div>
                <h4 className="font-medium mb-2 text-gray-700">Fee Breakdown</h4>
                <div className="space-y-2 text-sm max-w-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Tuition Fee</span>
                    <span>{formatINR(detail.feeStructure.fees.tuitionFee)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Development Fee</span>
                    <span>
                      {formatINR(detail.feeStructure.fees.developmentFee)}
                    </span>
                  </div>
                  <div className="flex justify-between font-semibold border-t pt-2">
                    <span>Total Fee</span>
                    <span>{formatINR(payable)}</span>
                  </div>
                </div>
              </div>
            )
          )}

          {renderTuitionPayments()}
        </div>
      )}

      {/* Inline receipts */}
      {showReceipts && (
        <div className="border-t p-5">
          <UnifiedReceiptViewer studentView filters={receiptFilters} />
        </div>
      )}
    </div>
  );
};

export default FeeYearCard;
